import React, { useState } from 'react';
import { 
  Settings as SettingsIcon,
  Smartphone,
  Shield,
  Bell,
  Database,
  Key,
  Globe,
  Save,
  RefreshCw,
  Eye,
  EyeOff
} from 'lucide-react';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [showApiKey, setShowApiKey] = useState(false);
  const [settings, setSettings] = useState({
    general: {
      companyName: 'VetmePay',
      supportEmail: 'support@vetmepay.com',
      timezone: 'Africa/Lagos',
      currency: 'NGN',
      language: 'en'
    },
    whatsapp: {
      phoneNumberId: '1234567890',
      accessToken: 'EAAT...',
      webhookVerifyToken: 'verify_token_123',
      appSecret: 'app_secret_456',
      apiVersion: 'v18.0'
    },
    security: {
      twoFactorEnabled: true,
      sessionTimeout: 30,
      maxLoginAttempts: 3,
      passwordExpiry: 90,
      apiRateLimit: 1000
    },
    notifications: {
      emailNotifications: true,
      transactionAlerts: true,
      securityAlerts: true,
      marketingEmails: false,
      pushNotifications: true
    },
    database: {
      mongoUri: 'mongodb://localhost:27017/vetme',
      backupFrequency: 'daily',
      retentionPeriod: 90,
      connectionPoolSize: 10
    }
  });

  const tabs = [
    { id: 'general', name: 'General', icon: SettingsIcon },
    { id: 'whatsapp', name: 'WhatsApp', icon: Smartphone },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'database', name: 'Database', icon: Database }
  ];

  const handleSettingChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const handleSave = () => {
    // Handle save logic here
    console.log('Saving settings:', settings);
  };

  const testWhatsAppConnection = () => {
    // Handle WhatsApp connection test
    console.log('Testing WhatsApp connection...');
  };

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-vetme-dark mb-4">Company Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
              Company Name
            </label>
            <input
              type="text"
              value={settings.general.companyName}
              onChange={(e) => handleSettingChange('general', 'companyName', e.target.value)}
              className="input-vetme w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
              Support Email
            </label>
            <input
              type="email"
              value={settings.general.supportEmail}
              onChange={(e) => handleSettingChange('general', 'supportEmail', e.target.value)}
              className="input-vetme w-full"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-vetme-dark mb-4">Localization</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
              Timezone
            </label>
            <select
              value={settings.general.timezone}
              onChange={(e) => handleSettingChange('general', 'timezone', e.target.value)}
              className="input-vetme w-full"
            >
              <option value="Africa/Lagos">Africa/Lagos (WAT)</option>
              <option value="UTC">UTC</option>
              <option value="America/New_York">America/New_York (EST)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
              Default Currency
            </label>
            <select
              value={settings.general.currency}
              onChange={(e) => handleSettingChange('general', 'currency', e.target.value)}
              className="input-vetme w-full"
            >
              <option value="NGN">Nigerian Naira (NGN)</option>
              <option value="USD">US Dollar (USD)</option>
              <option value="EUR">Euro (EUR)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
              Language
            </label>
            <select
              value={settings.general.language}
              onChange={(e) => handleSettingChange('general', 'language', e.target.value)}
              className="input-vetme w-full"
            >
              <option value="en">English</option>
              <option value="ha">Hausa</option>
              <option value="ig">Igbo</option>
              <option value="yo">Yoruba</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderWhatsAppSettings = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <Smartphone className="w-5 h-5 text-blue-600 mr-2" />
          <span className="text-sm font-medium text-blue-800">
            WhatsApp Business API Configuration
          </span>
        </div>
        <p className="text-sm text-blue-700 mt-1">
          Configure your WhatsApp Business API settings to enable messaging functionality.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
            Phone Number ID
          </label>
          <input
            type="text"
            value={settings.whatsapp.phoneNumberId}
            onChange={(e) => handleSettingChange('whatsapp', 'phoneNumberId', e.target.value)}
            className="input-vetme w-full"
            placeholder="Your WhatsApp Business phone number ID"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
            Access Token
          </label>
          <div className="relative">
            <input
              type={showApiKey ? "text" : "password"}
              value={settings.whatsapp.accessToken}
              onChange={(e) => handleSettingChange('whatsapp', 'accessToken', e.target.value)}
              className="input-vetme w-full pr-10"
              placeholder="Your WhatsApp Business API access token"
            />
            <button
              type="button"
              onClick={() => setShowApiKey(!showApiKey)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showApiKey ? (
                <EyeOff className="w-4 h-4 text-vetme-gray-dark" />
              ) : (
                <Eye className="w-4 h-4 text-vetme-gray-dark" />
              )}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
              Webhook Verify Token
            </label>
            <input
              type="text"
              value={settings.whatsapp.webhookVerifyToken}
              onChange={(e) => handleSettingChange('whatsapp', 'webhookVerifyToken', e.target.value)}
              className="input-vetme w-full"
              placeholder="Webhook verification token"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
              App Secret
            </label>
            <input
              type="password"
              value={settings.whatsapp.appSecret}
              onChange={(e) => handleSettingChange('whatsapp', 'appSecret', e.target.value)}
              className="input-vetme w-full"
              placeholder="App secret for webhook verification"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
            API Version
          </label>
          <select
            value={settings.whatsapp.apiVersion}
            onChange={(e) => handleSettingChange('whatsapp', 'apiVersion', e.target.value)}
            className="input-vetme w-full"
          >
            <option value="v18.0">v18.0 (Latest)</option>
            <option value="v17.0">v17.0</option>
            <option value="v16.0">v16.0</option>
          </select>
        </div>

        <div className="pt-4 border-t border-vetme-gray-light">
          <button
            onClick={testWhatsAppConnection}
            className="btn-vetme-outline"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Test Connection
          </button>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-vetme-dark mb-4">Authentication</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-vetme-dark">Two-Factor Authentication</div>
              <div className="text-sm text-vetme-gray-dark">Add an extra layer of security to your account</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.security.twoFactorEnabled}
                onChange={(e) => handleSettingChange('security', 'twoFactorEnabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-vetme-mint-pale rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-vetme-mint"></div>
            </label>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-vetme-dark mb-4">Session Management</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
              Session Timeout (minutes)
            </label>
            <input
              type="number"
              value={settings.security.sessionTimeout}
              onChange={(e) => handleSettingChange('security', 'sessionTimeout', parseInt(e.target.value))}
              className="input-vetme w-full"
              min="5"
              max="480"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
              Max Login Attempts
            </label>
            <input
              type="number"
              value={settings.security.maxLoginAttempts}
              onChange={(e) => handleSettingChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
              className="input-vetme w-full"
              min="1"
              max="10"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-vetme-dark mb-4">API Security</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
              API Rate Limit (requests/hour)
            </label>
            <input
              type="number"
              value={settings.security.apiRateLimit}
              onChange={(e) => handleSettingChange('security', 'apiRateLimit', parseInt(e.target.value))}
              className="input-vetme w-full"
              min="100"
              max="10000"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
              Password Expiry (days)
            </label>
            <input
              type="number"
              value={settings.security.passwordExpiry}
              onChange={(e) => handleSettingChange('security', 'passwordExpiry', parseInt(e.target.value))}
              className="input-vetme w-full"
              min="30"
              max="365"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-vetme-dark mb-4">Email Notifications</h3>
        <div className="space-y-4">
          {[
            { key: 'emailNotifications', label: 'Email Notifications', desc: 'Receive general email notifications' },
            { key: 'transactionAlerts', label: 'Transaction Alerts', desc: 'Get notified about transaction events' },
            { key: 'securityAlerts', label: 'Security Alerts', desc: 'Important security-related notifications' },
            { key: 'marketingEmails', label: 'Marketing Emails', desc: 'Promotional and marketing content' }
          ].map((notification) => (
            <div key={notification.key} className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-vetme-dark">{notification.label}</div>
                <div className="text-sm text-vetme-gray-dark">{notification.desc}</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.notifications[notification.key]}
                  onChange={(e) => handleSettingChange('notifications', notification.key, e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-vetme-mint-pale rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-vetme-mint"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-vetme-dark mb-4">Push Notifications</h3>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-vetme-dark">Push Notifications</div>
            <div className="text-sm text-vetme-gray-dark">Receive push notifications in your browser</div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.notifications.pushNotifications}
              onChange={(e) => handleSettingChange('notifications', 'pushNotifications', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-vetme-mint-pale rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-vetme-mint"></div>
          </label>
        </div>
      </div>
    </div>
  );

  const renderDatabaseSettings = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <Database className="w-5 h-5 text-yellow-600 mr-2" />
          <span className="text-sm font-medium text-yellow-800">
            Database Configuration
          </span>
        </div>
        <p className="text-sm text-yellow-700 mt-1">
          Changes to database settings require application restart.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
          MongoDB Connection URI
        </label>
        <input
          type="text"
          value={settings.database.mongoUri}
          onChange={(e) => handleSettingChange('database', 'mongoUri', e.target.value)}
          className="input-vetme w-full font-mono text-sm"
          placeholder="mongodb://localhost:27017/vetme"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
            Backup Frequency
          </label>
          <select
            value={settings.database.backupFrequency}
            onChange={(e) => handleSettingChange('database', 'backupFrequency', e.target.value)}
            className="input-vetme w-full"
          >
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
            Data Retention (days)
          </label>
          <input
            type="number"
            value={settings.database.retentionPeriod}
            onChange={(e) => handleSettingChange('database', 'retentionPeriod', parseInt(e.target.value))}
            className="input-vetme w-full"
            min="7"
            max="365"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-vetme-gray-dark mb-2">
          Connection Pool Size
        </label>
        <input
          type="number"
          value={settings.database.connectionPoolSize}
          onChange={(e) => handleSettingChange('database', 'connectionPoolSize', parseInt(e.target.value))}
          className="input-vetme w-full"
          min="5"
          max="50"
        />
        <p className="text-sm text-vetme-gray-dark mt-1">
          Number of concurrent database connections to maintain
        </p>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralSettings();
      case 'whatsapp':
        return renderWhatsAppSettings();
      case 'security':
        return renderSecuritySettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'database':
        return renderDatabaseSettings();
      default:
        return renderGeneralSettings();
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="vetme-heading-big text-vetme-dark">Settings</h1>
          <p className="mt-2 text-vetme-gray-dark">
            Configure your VetmePay admin dashboard and system preferences.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Settings Navigation */}
        <div className="lg:col-span-1">
          <div className="card-vetme p-4">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-vetme-mint-very-pale text-vetme-dark'
                        : 'text-vetme-gray-dark hover:bg-vetme-gray-lightest hover:text-vetme-dark'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-3">
          <div className="card-vetme p-6">
            {renderTabContent()}
            
            {/* Save Button */}
            <div className="mt-8 pt-6 border-t border-vetme-gray-light">
              <div className="flex justify-end space-x-3">
                <button className="btn-vetme-outline">
                  Reset to Defaults
                </button>
                <button onClick={handleSave} className="btn-vetme-primary">
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;